import { useState, useRef, useEffect } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiX } from 'react-icons/fi';
// import { useNavigate } from 'react-router-dom'; // Reserved for future navigation
import { careerService } from '../services/apiService';
import CareerForm from './CareerForm';
import { Users, Target, Briefcase, Award, Globe } from 'lucide-react';

const CareerList = () => {
  // const navigate = useNavigate(); // Reserved for future navigation
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const panelRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    loadCareers();
  }, []);

  const loadCareers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await careerService.getAllCareers();
      setCareers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load career data');
      setCareers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this career posting?')) {
      setIsDeleting(true);
      try {
        await careerService.deleteCareer(id);
        loadCareers();
      } catch (error) {
        setError(error.message || 'Failed to delete career');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (showForm) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (panelRef.current) panelRef.current.style.transform = 'translateX(0)';
        if (overlayRef.current) overlayRef.current.style.opacity = '1';
      }, 10);
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showForm]);

  const closePanel = () => {
    if (panelRef.current) panelRef.current.style.transform = 'translateX(100%)';
    if (overlayRef.current) overlayRef.current.style.opacity = '0';
    
    setTimeout(() => {
      setShowForm(false);
      setSelectedCareer(null);
    }, 300);
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Entry Level': return <Users className="w-4 h-4 mr-1" />;
      case 'Creative': return <Target className="w-4 h-4 mr-1" />;
      case 'Operations': return <Briefcase className="w-4 h-4 mr-1" />;
      case 'Leadership': return <Award className="w-4 h-4 mr-1" />;
      case 'Coordination': return <Globe className="w-4 h-4 mr-1" />;
      default: return <Briefcase className="w-4 h-4 mr-1" />;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading careers...</div>;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {error}
        <button 
          onClick={loadCareers}
          className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden relative">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Career Postings</h3>
        <button
          onClick={() => {
            setSelectedCareer(null);
            setShowForm(true);
          }}
          className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166534]"
        >
          <FiPlus className="mr-1" /> Create Career
        </button>
      </div>
      
      {careers.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg mb-4">No career postings found</p>
          <button
            onClick={() => {
              setSelectedCareer(null);
              setShowForm(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f]"
          >
            <FiPlus className="mr-2" /> Create Your First Career Posting
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Positions
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {careers.map((career) => (
                <tr key={career._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{career.title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-500">
                      {getCategoryIcon(career.category)}
                      {career.category}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{career.positions}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${career.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {career.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedCareer(career);
                        setShowForm(true);
                      }}
                      className="text-[#166534] hover:text-[#0e3d1f] mr-3"
                      title="Edit career"
                    >
                      <FiEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(career._id)}
                      disabled={isDeleting}
                      className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      title="Delete career"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Sliding Panel */}
      {showForm && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div 
              ref={overlayRef}
              className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out opacity-0"
              onClick={closePanel}
            />
            
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
              <div
                ref={panelRef}
                className="relative w-screen max-w-md transform transition-transform duration-300 ease-in-out translate-x-full"
              >
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll">
                  <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedCareer ? 'Edit Career' : 'Create New Career'}
                    </h3>
                    <button
                      onClick={closePanel}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none transition-colors duration-200"
                    >
                      <FiX className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="flex-1 p-4">
                    <CareerForm
                      career={selectedCareer}
                      onClose={closePanel}
                      onSave={() => {
                        loadCareers();
                        closePanel();
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareerList;